import * as React from 'react';
import styled from 'styled-components';

import { compose } from '@doltech/utils/lib/compose';
import { verticalSpaceLineDivider } from '@doltech/utils/lib/css-style';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

import { colorsV2 } from '../../../figma/colors-v2';
import { shadows } from '../../../figma/shadows';
import { findBellowByType } from '../../../figma/DolEditor/helpers/nodeValue';
import { QuizBoxItemButtonSingleQuiz } from '../../../figma/BlogEditorBlogSystem/QuizBoxItemButtonSingleQuiz/QuizBoxItemButtonSingleQuiz';
import { withRootElement } from '../../../slatePlugins/common/hocs/current-element.store';
import { useProgressListGrammar, withProgressListGrammar } from '../../hocs/progress-list-grammar';
import { EXERCISE_MULTIPLE_CHOICE_OPTIONS, EXERCISE_MULTIPLE_CHOICE_QUESTION } from '../types';

const Container = styled.div`
  padding: 20px;
  background-color: ${colorsV2.white100};
  border: 1px solid ${colorsV2.neutral40};
  border-radius: 16px;
  box-shadow: ${shadows.neutralShadowToBot4};

  p {
    display: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
    margin: inherit;
  }

  ${verticalSpaceLineDivider({
    space: 16,
    lineSize: 1,
    lineColor: colorsV2.neutral20,
  })}
`;

interface MultipleChoiceElementProps {
  children?: React.ReactNode;
  progressList?: any[];
  element?: any;

  onCheckAnswer?: (value: any) => void;
  onNeedToSignIn?: () => void;
}

const MultipleChoiceElementBase = (props: MultipleChoiceElementProps) => {
  const { children, progressList, element, onCheckAnswer, onNeedToSignIn } = props;
  const { isLoggedIn } = useGetAuthInfo();
  const progressStore = useProgressListGrammar();

  const userAnswers = progressList
    ?.find((progress) => element?.id === progress?.exerciseId)
    ?.userAnswers;

  const questionNodeList = findBellowByType(element, EXERCISE_MULTIPLE_CHOICE_QUESTION);
  const correctUserAnswers = (progressStore?.userAnswers || []).filter((eachAnswer) => eachAnswer.isCorrect);

  const totalQuestions = questionNodeList?.length;
  const totalCorrect = correctUserAnswers?.length;

  let status: 'default' | 'done' | 'done-correct-all' = 'default';
  let alertText = '';

  if (progressStore?.isShowResult) {
    if (totalCorrect === totalQuestions) {
      status = 'done-correct-all';
      alertText = 'All correct!';
    } else {
      status = 'done';
      alertText = `You got ${totalCorrect}/${totalQuestions} correct `;
    }
  }

  React.useEffect(() => {
    if (userAnswers?.length && !progressStore.initialed) {
      progressStore.initProgress({ userAnswers });
      progressStore.setIsShowResult(true);
    }
  }, [userAnswers, progressStore]);

  return (
    <Container className="dol-editor-exercise multiple-choice-element"> {/* Do not remove class dol-editor-exercise -> Some logic depend on this class */}
      {children}
      <QuizBoxItemButtonSingleQuiz
        status={status}
        alertText={alertText}
        onCheckAnswer={() => {
          if (isLoggedIn) {
            const updatedUserAnswers = progressStore?.userAnswers?.map(eachAnswer => {
              const currentQuestion = questionNodeList
                ?.find((eachQuestion) => eachQuestion.id === eachAnswer.questionId);
              const options = currentQuestion?.children?.find(ele => EXERCISE_MULTIPLE_CHOICE_OPTIONS === ele?.type)?.children;
              const correctOptionIds = options
                ?.filter((eachOption) => eachOption.isCorrect)
                ?.map((eachCorrectOption) => eachCorrectOption?.id);

              return {
                questionId: eachAnswer?.questionId,
                answerIds: eachAnswer?.answerIds,
                isCorrect: (eachAnswer?.answerIds?.length === correctOptionIds?.length)
                  && eachAnswer.answerIds.every((eachAnswerId) => correctOptionIds?.includes(eachAnswerId)),
              };
            });
            progressStore.setUserAnswers(updatedUserAnswers);
            progressStore?.setIsShowResult(true);
            onCheckAnswer?.({
              exerciseId: element?.id,
              type: 'MULTIPLE_CHOICE',
              userAnswers: updatedUserAnswers,
            });
          } else {
            onNeedToSignIn();
          }
        }}
        onRedo={() => {
          progressStore?.setUserAnswers([]);
          progressStore?.setIsShowResult(false);
        }}
        disabled={!progressStore?.userAnswers?.length}
      />
    </Container>
  );
};

export const MultipleChoiceElement = compose(
  withRootElement,
  withProgressListGrammar,
)(MultipleChoiceElementBase);
